import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import { useAuth, type AuthUser } from '../context/AuthContext';
import { TAG_PILL_COLORS, ROLE_LABELS } from '../constants/taskTags';
import { updateMyUsername } from '../api/users';
import { SessionExpiredError } from '../api/session';
import {
  BACKUP_KIND_LABELS,
  BACKUP_KIND_TITLES,
  BACKUP_TAG_LABELS,
  BACKUP_TAGS,
  type BackupEntry,
  type BackupListResponse,
  type BackupTag,
  backupDownloadUrl,
  fetchBackups,
  formatBackupSize,
  formatBackupTimestamp,
  runBackupNow,
} from '../api/backups';
import './Page.css';
import './Settings.css';

function getUserRoles(user: { roles?: string[]; role?: string }): string[] {
  if (Array.isArray(user.roles) && user.roles.length > 0) return user.roles;
  if (user.role) return [user.role];
  return ['sales'];
}

interface BackupsCardProps {
  adminUserId: number;
}

const BackupsCard: React.FC<BackupsCardProps> = ({ adminUserId }) => {
  const [data, setData] = useState<BackupListResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busyTag, setBusyTag] = useState<BackupTag | null>(null);
  const [filterTag, setFilterTag] = useState<BackupTag | 'all'>('all');

  const refresh = useCallback(async () => {
    try {
      const next = await fetchBackups(adminUserId);
      setData(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load backups.');
    } finally {
      setLoading(false);
    }
  }, [adminUserId]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  const latestByTag = useMemo<Record<BackupTag, BackupEntry | undefined>>(() => {
    const out: Partial<Record<BackupTag, BackupEntry>> = {};
    if (data) {
      for (const tag of BACKUP_TAGS) {
        // Prefer the DB dump for the tier's "Latest" widget; each run also
        // produces tarballs (uploads, File Drive) with slightly later timestamps.
        out[tag] =
          data.backups.find((b) => b.tag === tag && b.kind === 'db') ??
          data.backups.find((b) => b.tag === tag);
      }
    }
    return out as Record<BackupTag, BackupEntry | undefined>;
  }, [data]);

  const visibleBackups = useMemo<BackupEntry[]>(() => {
    if (!data) return [];
    if (filterTag === 'all') return data.backups;
    return data.backups.filter((b) => b.tag === filterTag);
  }, [data, filterTag]);

  const handleRun = async (tag: BackupTag) => {
    setBusyTag(tag);
    setError(null);
    try {
      await runBackupNow(adminUserId, tag);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Backup failed.');
    } finally {
      setBusyTag(null);
    }
  };

  return (
    <div className="settings-card">
      <h3 className="settings-card-title">Database Backups</h3>
      <div className="settings-card-body">
        <p className="settings-hint">
          Snapshots of the PostgreSQL database (custom-format <code>pg_dump</code>). Schedule:
          hourly, daily 02:15, weekly Sunday 02:30, monthly 1st 02:45 (Pacific Time). Each run
          updates the stable <code>&lt;db&gt;-&lt;tier&gt;.dump</code> file in the dumps folder
          and adds a timestamped copy to <code>archive/&lt;tier&gt;/</code>; older history is
          trimmed to the per-tier retention shown below.
        </p>
        <p className="settings-hint">
          Runs also tar the files stored on disk: attachments and photos every run, and the File
          Drive on the daily, weekly and monthly tiers (its own shorter history, since those files
          can be large).
        </p>
        {data && !data.scheduleEnabled && (
          <p className="settings-message settings-message--error">
            Scheduled backups are disabled (DISABLE_BACKUPS=1). Manual runs still work.
          </p>
        )}
        {error && <p className="settings-message settings-message--error">{error}</p>}

        <div className="backups-tier-grid">
          {BACKUP_TAGS.map((tag) => {
            const latest = latestByTag[tag];
            const keep = data?.retention[tag];
            const isRunning = busyTag === tag || (data?.running[tag] ?? false);
            return (
              <div className="backups-tier-card" key={tag}>
                <div className="backups-tier-header">
                  <span className="backups-tier-label">{BACKUP_TAG_LABELS[tag]}</span>
                  {keep != null && (
                    <span className="backups-tier-retention">keep {keep}</span>
                  )}
                </div>
                <div className="backups-tier-meta">
                  {latest ? (
                    <>
                      <span className="backups-tier-meta-label">Latest</span>
                      <span className="backups-tier-meta-value">
                        {formatBackupTimestamp(latest.createdAt)}
                      </span>
                      <span className="backups-tier-meta-sub">
                        {formatBackupSize(latest.size)}
                      </span>
                    </>
                  ) : (
                    <span className="backups-tier-meta-empty">No backup yet</span>
                  )}
                </div>
                <button
                  type="button"
                  className="backups-run-button"
                  disabled={isRunning}
                  onClick={() => void handleRun(tag)}
                >
                  {isRunning ? 'Running…' : 'Run now'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="backups-toolbar">
          <div className="backups-filter-row" role="tablist" aria-label="Filter backups by tier">
            <button
              type="button"
              className={`backups-filter-pill${filterTag === 'all' ? ' backups-filter-pill--active' : ''}`}
              onClick={() => setFilterTag('all')}
            >
              All
            </button>
            {BACKUP_TAGS.map((tag) => (
              <button
                type="button"
                key={tag}
                className={`backups-filter-pill${filterTag === tag ? ' backups-filter-pill--active' : ''}`}
                onClick={() => setFilterTag(tag)}
              >
                {BACKUP_TAG_LABELS[tag]}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="backups-refresh-button"
            onClick={() => void refresh()}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        <div className="backups-table-wrap">
          <table className="backups-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>File</th>
                <th>Created</th>
                <th>Size</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr>
                  <td colSpan={5} className="backups-empty">
                    Loading…
                  </td>
                </tr>
              ) : visibleBackups.length === 0 ? (
                <tr>
                  <td colSpan={5} className="backups-empty">
                    No backups yet. Click <strong>Run now</strong> on a tier above to create one.
                  </td>
                </tr>
              ) : (
                visibleBackups.map((entry) => (
                  <tr key={`${entry.tag}/${entry.name}`}>
                    <td>
                      <span className={`backups-tier-pill backups-tier-pill--${entry.tag}`}>
                        {BACKUP_TAG_LABELS[entry.tag]}
                      </span>
                    </td>
                    <td className="backups-file-cell">
                      <span
                        className={`backups-kind-pill backups-kind-pill--${entry.kind}`}
                        title={BACKUP_KIND_TITLES[entry.kind]}
                      >
                        {BACKUP_KIND_LABELS[entry.kind]}
                      </span>
                      {entry.name}
                    </td>
                    <td>{formatBackupTimestamp(entry.createdAt)}</td>
                    <td>{formatBackupSize(entry.size)}</td>
                    <td className="backups-actions-cell">
                      <a
                        className="backups-action-link"
                        href={backupDownloadUrl(adminUserId, entry.tag, entry.name)}
                      >
                        Download
                      </a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {data?.storagePath && (
          <details className="backups-restore-help">
            <summary>How to restore a dump</summary>
            <p>
              Files live at <code>{data.storagePath}</code>. The folder is git-ignored, so backups
              never end up in commits and dev/prod environments don't conflict. Each run produces
              two files per tier: a <strong>Database</strong> dump
              (<code>&lt;db&gt;-&lt;tier&gt;.dump</code>) and an <strong>Uploads</strong> tarball
              (<code>&lt;db&gt;-&lt;tier&gt;-uploads.tar.gz</code>) holding the on-disk attachments
              and photos that live outside Postgres. Both keep a stable "latest" copy in the root
              plus timestamped history under <code>archive/&lt;tier&gt;/</code>.
            </p>
            <pre className="backups-restore-code">
{`# Safer: restore into a fresh database first, then copy rows back.
createdb techforce_restore
pg_restore -d techforce_restore /path/to/backup.dump

# Or restore directly over the live database (destructive):
pg_restore --clean --if-exists -d techforce_portal /path/to/backup.dump

# Restore uploaded files (attachments/photos) into server/uploads:
tar -xzf /path/to/<db>-<tier>-uploads.tar.gz -C /path/to/server`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

/**
 * Account details. The username doubles as the display name on tasks, orders and
 * emails, so it is editable here while the email (the login) is not.
 */
const AccountCard: React.FC<{ user: AuthUser }> = ({ user }) => {
  const { updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.username);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const startEditing = () => {
    setValue(user.username);
    setError(null);
    setSaved(false);
    setEditing(true);
  };

  const save = async () => {
    const next = value.trim().replace(/\s+/g, ' ');
    if (!next || next === user.username) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await updateMyUsername(next);
      updateUser({ username: updated.username });
      setEditing(false);
      setSaved(true);
    } catch (e) {
      setError(
        e instanceof SessionExpiredError
          ? 'Please sign out and sign back in, then try again.'
          : e instanceof Error
          ? e.message
          : 'Failed to update your name.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings-card">
      <h3 className="settings-card-title">Account</h3>
      <div className="settings-card-body">
        <div className="settings-field-row">
          <div className="settings-field">
            <span className="settings-field-label">Username</span>
            {editing ? (
              <div className="settings-inline-edit">
                <input
                  className="settings-input"
                  value={value}
                  maxLength={60}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void save();
                    if (e.key === 'Escape') setEditing(false);
                  }}
                  aria-label="Username"
                  autoFocus
                />
                <button type="button" className="settings-inline-button" disabled={busy} onClick={() => void save()}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="settings-inline-button settings-inline-button--quiet"
                  disabled={busy}
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="settings-inline-edit">
                <span className="settings-field-value">{user.username}</span>
                <button type="button" className="settings-inline-button" onClick={startEditing}>
                  Edit
                </button>
              </div>
            )}
          </div>
          <div className="settings-field">
            <span className="settings-field-label">Email</span>
            <span className="settings-field-value">{user.email}</span>
          </div>
        </div>
        <p className="settings-hint">
          Your username is the name teammates see on tasks, orders and notifications. Your email is your sign-in and
          can only be changed by an administrator.
        </p>
        {error && <p className="settings-message settings-message--error">{error}</p>}
        {saved && <p className="settings-message settings-message--success">Your name has been updated.</p>}
      </div>
    </div>
  );
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!user) return null;

  const roles = [...getUserRoles(user)].sort((a, b) => a.localeCompare(b));
  const isAdmin = roles.includes('admin');

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <div>
            <PageHeader title="Settings" subtitle="Manage your account and preferences" />
          </div>

          <div className="page-content settings-content">
            <AccountCard user={user} />

            {/* Roles card — read-only */}
            <div className="settings-card">
              <h3 className="settings-card-title">Roles</h3>
              <div className="settings-card-body">
                <p className="settings-hint">
                  Your roles are assigned by TFRI. Contact an administrator if you need different access.
                </p>

                <div className="settings-roles-readonly" aria-label="Your roles">
                  {roles.map((role) => (
                    <span
                      key={role}
                      className="settings-role-readonly-pill"
                      style={
                        TAG_PILL_COLORS[role]
                          ? { backgroundColor: TAG_PILL_COLORS[role], color: '#1B1C1E' }
                          : undefined
                      }
                    >
                      {ROLE_LABELS[role] ?? role.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>

                <div className="settings-actions">
                  <button type="button" className="cancel-button" onClick={() => navigate(-1)}>
                    Back
                  </button>
                </div>
              </div>
            </div>

            {isAdmin && <BackupsCard adminUserId={user.id} />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Settings;
